// Contract with POST/PUT /api/it/assets. Technical and commercial extras live in `specs`.

export const ASSET_STATUSES = [
  { code: 'activo', name: 'Activo' },
  { code: 'en_reparacion', name: 'En reparación' },
  { code: 'en_prestamo', name: 'En préstamo' },
  { code: 'reemplazado', name: 'Reemplazado' },
  { code: 'extraviado', name: 'Extraviado' },
  { code: 'dado_de_baja', name: 'Dado de baja' },
];

export const ASSET_CONDITIONS = [
  { code: 'bueno', name: 'Bueno' },
  { code: 'regular', name: 'Regular' },
  { code: 'malo', name: 'Malo' },
];

export const COMMERCIAL_STATUSES = ['cotizado', 'vendido', 'instalado', 'en_soporte', 'retirado'];

export const DEFAULT_ASSET_CATEGORIES = [
  'laptop', 'desktop', 'server', 'printer', 'router', 'switch', 'firewall',
  'access-point', 'camera', 'ups', 'monitor', 'phone', 'tablet', 'other',
].map((code) => ({ code, name: code.replace('-', ' ').replace(/^\w/, (c) => c.toUpperCase()) }));

export const FINANCIAL_FIELDS = [
  { key: 'unit_cost', label: 'Costo Unitario' },
  { key: 'import_cost', label: 'Costo Importación' },
  { key: 'installation_cost', label: 'Costo Instalación' },
  { key: 'configuration_cost', label: 'Costo Configuración' },
  { key: 'logistics_cost', label: 'Costo Logístico' },
  { key: 'other_costs', label: 'Otros Costos' },
  { key: 'client_price', label: 'Precio al Cliente' },
  { key: 'taxes', label: 'Impuestos' },
];

const COST_KEYS = ['unit_cost', 'import_cost', 'installation_cost', 'configuration_cost', 'logistics_cost', 'other_costs'];

export const EMPTY_FINANCIALS = {
  unit_cost: 0, import_cost: 0, other_costs: 0, installation_cost: 0, configuration_cost: 0,
  logistics_cost: 0, client_price: 0, currency: 'USD', taxes: 0,
};

const EMPTY_SPECS = {
  hostname: '', internal_code: '', subcategory: '', commercial_status: 'instalado',
  area: '', detailed_location: '', client_contact: '', provider: '',
  operating_system: '', processor: '', ram: '', storage: '', ip: '', mac: '',
  install_date: '', warranty_start: '',
};

export const EMPTY_ASSET = {
  name: '', category: 'laptop', brand: '', model: '', serial_number: '', tag: '',
  status: 'activo', condition: 'bueno', client_id: '', location_id: '', assigned_to: '',
  purchase_date: '', warranty_expiry: '', notes: '',
  specs: { ...EMPTY_SPECS, financials: { ...EMPTY_FINANCIALS } },
};

// Builds the form state from an API asset (or the empty form).
export function assetToForm(asset) {
  if (!asset) return { ...EMPTY_ASSET, specs: { ...EMPTY_SPECS, financials: { ...EMPTY_FINANCIALS } } };
  const specs = asset.specs || {};
  return {
    ...EMPTY_ASSET,
    name: asset.name || '',
    category: asset.category || EMPTY_ASSET.category,
    brand: asset.brand || '',
    model: asset.model || '',
    serial_number: asset.serial_number || '',
    tag: asset.tag || '',
    status: asset.status || EMPTY_ASSET.status,
    condition: asset.condition || EMPTY_ASSET.condition,
    client_id: asset.client_id || '',
    location_id: asset.location_id || '',
    assigned_to: asset.assigned_to || '',
    purchase_date: asset.purchase_date || '',
    warranty_expiry: asset.warranty_expiry || '',
    notes: asset.notes || '',
    specs: {
      ...EMPTY_SPECS,
      ...specs,
      financials: { ...EMPTY_FINANCIALS, ...(specs.financials || {}) },
    },
  };
}

// Drops empty strings so optional dates/ids are not sent as "".
export function assetFormToPayload(form) {
  const clean = (value) => (typeof value === 'string' ? value.trim() : value);
  const optional = (value) => (clean(value) === '' ? null : clean(value));
  return {
    name: clean(form.name) || [clean(form.brand), clean(form.model)].filter(Boolean).join(' ') || clean(form.tag) || clean(form.serial_number),
    category: form.category,
    brand: optional(form.brand),
    model: optional(form.model),
    serial_number: optional(form.serial_number),
    tag: optional(form.tag),
    status: form.status,
    condition: form.condition,
    client_id: form.client_id,
    location_id: optional(form.location_id),
    assigned_to: optional(form.assigned_to),
    purchase_date: optional(form.purchase_date),
    warranty_expiry: optional(form.warranty_expiry),
    notes: optional(form.notes),
    specs: form.specs,
  };
}

export function assetDisplayName(asset) {
  return asset.name || [asset.brand, asset.model].filter(Boolean).join(' ') || asset.tag || asset.serial_number || 'Activo';
}

export function assetStatusColor(status) {
  const colors = {
    activo: 'bg-green-500/20 text-green-400 border-green-500/30',
    en_reparacion: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    en_prestamo: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    reemplazado: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    extraviado: 'bg-red-500/20 text-red-400 border-red-500/30',
    dado_de_baja: 'bg-gray-600/20 text-gray-500 border-gray-600/30',
  };
  return colors[status] || 'bg-gray-500/20 text-gray-400';
}

export function assetFinancialSummary(asset) {
  const financials = asset.specs?.financials || {};
  const totalCost = COST_KEYS.reduce((sum, key) => sum + (Number(financials[key]) || 0), 0);
  const clientPrice = Number(financials.client_price) || 0;
  const profit = clientPrice - totalCost;
  const margin = clientPrice ? ((profit / clientPrice) * 100).toFixed(1) : '0.0';
  return { financials, totalCost, clientPrice, profit, margin };
}

export function assetStats(assets) {
  const count = (status) => assets.filter((asset) => asset.status === status).length;
  return {
    total: assets.length,
    active: count('activo'),
    inRepair: count('en_reparacion'),
    retired: count('dado_de_baja'),
  };
}

export const ASSET_HISTORY_LABELS = {
  created: { icon: '🆕', label: 'Creado' },
  updated: { icon: '✏️', label: 'Actualizado' },
  deleted: { icon: '🗑️', label: 'Eliminado' },
  maintenance_executed: { icon: '🛠️', label: 'Mantenimiento ejecutado' },
};

// Immutable update for paths like "brand", "specs.ram" or "specs.financials.unit_cost".
export function setAssetField(form, path, value) {
  const keys = path.split('.');
  const update = (node, index) => ({
    ...node,
    [keys[index]]: index === keys.length - 1 ? value : update(node[keys[index]] || {}, index + 1),
  });
  return update(form, 0);
}
